import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { UserIcon, TrophyIcon, StarIcon, ChartBarIcon, CurrencyDollarIcon, GiftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

function Profile() {
  const { user, loading } = useAuth();
  const { leaderboard } = useGame();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#000033] to-[#000066] p-6 flex items-center justify-center">
        <div className="text-white text-center">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#000033] to-[#000066] p-6 flex items-center justify-center">
        <div className="text-white text-center">
          <p>Please log in to view your profile</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-[#4CD4C0] text-[#000033] rounded-full"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  const userStats = {
    username: user?.username || 'Guest',
    registrationDate: user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A',
    quizzesTaken: user?.quizzesTaken || 0,
    totalScore: user?.totalScore || 0,
    coins: user?.coins || 0,
    spinsPlayed: user?.spinsPlayed || 0,
    totalWinnings: user?.totalWinnings || 0,
    rank: leaderboard?.findIndex(entry => entry.userId === user?.id) + 1 || 'N/A'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#000033] to-[#000066] p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-[#000045]/80 backdrop-blur-sm rounded-2xl p-6 border border-[#4CD4C0]/20">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-[#4CD4C0]/20 flex items-center justify-center">
              <UserIcon className="h-10 w-10 text-[#4CD4C0]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{userStats.username}</h2>
              <p className="text-gray-400">Member since {userStats.registrationDate}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#000045]/80 backdrop-blur-sm rounded-2xl p-6 border border-[#4CD4C0]/20">
            <div className="flex items-center gap-3 mb-4">
              <TrophyIcon className="h-5 w-5 text-[#4CD4C0]" />
              <h3 className="text-lg font-semibold text-white">Quiz Stats</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#000033]/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <StarIcon className="h-5 w-5 text-[#4CD4C0]" />
                  <span className="text-white">Quizzes Taken</span>
                </div>
                <span className="text-white font-semibold">{userStats.quizzesTaken}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#000033]/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <ChartBarIcon className="h-5 w-5 text-[#4CD4C0]" />
                  <span className="text-white">Total Score</span>
                </div>
                <span className="text-white font-semibold">{userStats.totalScore}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#000033]/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <TrophyIcon className="h-5 w-5 text-[#4CD4C0]" />
                  <span className="text-white">Rank</span>
                </div>
                <span className="text-white font-semibold">{userStats.rank}</span>
              </div>
            </div>
          </div>

          <div className="bg-[#000045]/80 backdrop-blur-sm rounded-2xl p-6 border border-[#4CD4C0]/20">
            <h3 className="text-lg font-semibold text-white mb-4">Rewards</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-[#000033]/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <CurrencyDollarIcon className="h-5 w-5 text-[#4CD4C0]" />
                  <span className="text-white">Coins</span>
                </div>
                <span className="text-white font-semibold">{userStats.coins}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#000033]/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <GiftIcon className="h-5 w-5 text-[#4CD4C0]" />
                  <span className="text-white">Spins Played</span>
                </div>
                <span className="text-white font-semibold">{userStats.spinsPlayed}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-[#000033]/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <TrophyIcon className="h-5 w-5 text-[#4CD4C0]" />
                  <span className="text-white">Total Winnings</span>
                </div>
                <span className="text-white font-semibold">{userStats.totalWinnings}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#000045]/80 backdrop-blur-sm rounded-2xl p-6 border border-[#4CD4C0]/20">
          <h3 className="text-lg font-semibold text-white mb-4">Achievements</h3>
          <div className="space-y-4">
            {user?.achievements?.length > 0 ? (
              user.achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className="flex items-center justify-between p-4 bg-[#000033]/50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{achievement.icon}</span>
                    <div>
                      <p className="text-white font-medium">{achievement.title}</p>
                      <p className="text-gray-400 text-sm">{achievement.description}</p>
                    </div>
                  </div>
                  <span className="text-[#4CD4C0] font-semibold">
                    +{achievement.reward} coins
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">No achievements yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile; 